import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { canDelete } from "@/utils/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ShoppingCart, Plus, Trash2, Eye, ChevronLeft, ArrowDownCircle,
  DollarSign, Calendar, User, Package, CheckCircle, AlertTriangle,
  FileSpreadsheet, FileText,
} from "lucide-react";
import { ModuleHeaderIcon } from "@/components/ModuleHeader";

const Pedidos = () => {
  const { user } = useAuth();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [detail, setDetail] = useState(null);
  const [newForm, setNewForm] = useState({ nombre: "", responsable: "", notas: "" });
  const [showAddItem, setShowAddItem] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [addQty, setAddQty] = useState(1);

  const fetchPedidos = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/pedidos`, { withCredentials: true });
      setPedidos(res.data);
    } catch (err) { console.error("Error loading pedidos:", err); toast.error("Error al cargar pedidos"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPedidos(); }, [fetchPedidos]);

  const fetchAllItems = async () => {
    try {
      const res = await axios.get(`${API}/inventory`, { withCredentials: true });
      setAllItems(res.data);
    } catch (err) { console.error("Error fetching items:", err); }
  };

  const handleOpenAddItem = () => {
    fetchAllItems();
    setSelectedItemId("");
    setAddQty(1);
    setShowAddItem(true);
  };

  const handleAddItem = async () => {
    if (!selectedItemId) { toast.error("Selecciona un producto"); return; }
    try {
      const res = await axios.post(`${API}/pedidos/${detail.id}/add-item`, {
        item_id: selectedItemId,
        cantidad: Math.max(1, Math.ceil(addQty || 1)),
      }, { withCredentials: true });
      toast.success("Producto agregado al pedido");
      setDetail(res.data);
      setShowAddItem(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al agregar"); }
  };

  const handleGenerar = async () => {
    try {
      const res = await axios.post(`${API}/pedidos/generar`, newForm, { withCredentials: true });
      if ((res.data.items || []).length === 0) {
        toast.info("No hay productos por debajo del mínimo");
        return;
      }
      toast.success(`Pedido generado con ${res.data.items.length} productos`);
      setShowNewDialog(false);
      setNewForm({ nombre: "", responsable: "", notas: "" });
      setDetail(res.data);
      fetchPedidos();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const openDetail = async (p) => {
    try {
      const res = await axios.get(`${API}/pedidos/${p.id}`, { withCredentials: true });
      setDetail(res.data);
    } catch (err) { console.error("Error opening pedido:", err); toast.error("Error"); }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar este pedido?")) return;
    try {
      await axios.delete(`${API}/pedidos/${id}`, { withCredentials: true });
      toast.success("Pedido eliminado"); setDetail(null); fetchPedidos();
    } catch (err) { console.error("Error deleting pedido:", err); toast.error("Error"); }
  };

  const updateItemQty = (idx, val) => {
    const items = [...(detail.items || [])];
    const qty = val === "" ? 0 : Math.ceil(parseInt(val) || 0);
    items[idx] = { ...items[idx], cantidad: qty, subtotal: Math.round(qty * items[idx].costo_unitario * 100) / 100 };
    const total = Math.round(items.reduce((s, i) => s + i.subtotal, 0) * 100) / 100;
    setDetail({ ...detail, items, total });
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/pedidos/${detail.id}`, { items: detail.items, total: detail.total }, { withCredentials: true });
      toast.success("Pedido guardado");
    } catch (err) { console.error("Error saving pedido:", err); toast.error("Error al guardar"); }
  };

  const removeItem = (idx) => {
    const items = [...(detail.items || [])];
    items.splice(idx, 1);
    const total = Math.round(items.reduce((s, i) => s + (i.subtotal || 0), 0) * 100) / 100;
    setDetail({ ...detail, items, total });
  };

  const handleConvertir = async () => {
    if (!confirm("¿Convertir pedido en entrada de productos? El stock se actualizará automáticamente.")) return;
    try {
      const res = await axios.post(`${API}/pedidos/${detail.id}/convertir`, {}, { withCredentials: true });
      toast.success("Pedido convertido en entrada. Stock actualizado.");
      setDetail(res.data);
      fetchPedidos();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleExport = async (kind) => {
    try {
      const res = await axios.get(`${API}/pedidos/${detail.id}/export-${kind}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="([^"]+)"/);
      const fallbackExt = kind === "pdf" ? "pdf" : "xlsx";
      const filename = match ? match[1] : `pedido_${detail.id}.${fallbackExt}`;
      const blob = new Blob([res.data], {
        type: kind === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} descargado`);
    } catch {
      toast.error("Error al exportar");
    }
  };

  const fmt = (v) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
  const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }); }
    catch (err) { console.error("Date format error:", err); return d; }
  };

  // ============ DETAIL VIEW ============
  if (detail) {
    const items = detail.items || [];
    const isConverted = detail.estatus === "convertido";

    return (
      <div className="p-4 md:p-6 lg:p-8" data-testid="pedido-detail">
        <div className="max-w-7xl mx-auto space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => { setDetail(null); fetchPedidos(); }} data-testid="back-btn">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">{detail.nombre}</h1>
                <p className="text-sm text-slate-500">{fmtDate(detail.fecha)} {detail.responsable && `· ${detail.responsable}`}</p>
              </div>
              <Badge className={isConverted ? "bg-green-500 text-white" : "bg-amber-500 text-white"}>
                {isConverted ? "Convertido" : "Pendiente"}
              </Badge>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                onClick={() => handleExport("xlsx")}
                data-testid="export-pedido-xlsx-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
              </Button>
              <Button
                variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                onClick={() => handleExport("pdf")}
                data-testid="export-pedido-pdf-btn"
              >
                <FileText className="w-4 h-4 mr-2" /> PDF
              </Button>
              {!isConverted && (
                <>
                  <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-pedido-btn">Guardar</Button>
                  <Button variant="outline" className="border-emerald-300 text-emerald-700" onClick={handleOpenAddItem} data-testid="add-item-btn">
                    <Plus className="w-4 h-4 mr-2" /> Añadir Artículo
                  </Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleConvertir} data-testid="convertir-btn">
                    <ArrowDownCircle className="w-4 h-4 mr-2" /> Convertir en Entrada
                  </Button>
                </>
              )}
              {!isConverted && canDelete(user) && (
                <Button variant="ghost" className="text-red-600" onClick={() => handleDelete(detail.id)}><Trash2 className="w-4 h-4" /></Button>
              )}
            </div>
          </div>

          {/* Total Card */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg"><DollarSign className="w-5 h-5 text-blue-600" /></div>
                <div><p className="text-xs text-slate-500">Total Pedido</p><p className="text-xl font-bold text-blue-700">{fmt(detail.total)}</p></div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg"><Package className="w-5 h-5 text-amber-600" /></div>
                <div><p className="text-xs text-slate-500">Productos</p><p className="text-xl font-bold">{items.length}</p></div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg"><ShoppingCart className="w-5 h-5 text-emerald-600" /></div>
                <div><p className="text-xs text-slate-500">Total Unidades</p><p className="text-xl font-bold">{items.reduce((s, i) => s + (i.cantidad || 0), 0)}</p></div>
              </CardContent>
            </Card>
          </div>

          {isConverted && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm">
              <CheckCircle className="w-5 h-5" /> Este pedido ya fue convertido en entrada. El stock fue actualizado.
            </div>
          )}

          {/* Items Table */}
          <Card>
            <CardHeader><CardTitle className="text-lg">Productos del Pedido</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>CLAVE</TableHead>
                    <TableHead>DESCRIPCIÓN</TableHead>
                    <TableHead>ARTÍCULO</TableHead>
                    <TableHead>PRESENTACIÓN</TableHead>
                    <TableHead>UNIDAD</TableHead>
                    <TableHead className="text-center">STOCK ACT.</TableHead>
                    <TableHead className="text-center">STOCK MÍN.</TableHead>
                    <TableHead className="text-center w-28">PEDIR</TableHead>
                    <TableHead className="text-right">COSTO UNIT.</TableHead>
                    <TableHead className="text-right">SUBTOTAL</TableHead>
                    {!isConverted && <TableHead className="w-10"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow><TableCell colSpan={11} className="text-center py-10 text-slate-400">Sin productos en el pedido</TableCell></TableRow>
                  ) : items.map((it, idx) => (
                    <TableRow key={it.item_id} className="bg-red-50/30">
                      <TableCell className="font-mono font-semibold text-sm">{it.item_clave}</TableCell>
                      <TableCell className="text-sm">{it.item_descripcion}</TableCell>
                      <TableCell className="text-sm text-slate-500">{it.item_articulo}</TableCell>
                      <TableCell className="text-sm text-slate-500">{it.presentacion || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-500">{it.unidad || "—"}</TableCell>
                      <TableCell className="text-center">
                        <span className="text-red-600 font-bold">{it.stock_actual}</span>
                      </TableCell>
                      <TableCell className="text-center font-semibold">{it.stock_minimo}</TableCell>
                      <TableCell className="text-center">
                        {isConverted ? (
                          <span className="font-bold">{it.cantidad}</span>
                        ) : (
                          <Input
                            type="number" min="0" step="1"
                            value={it.cantidad === 0 ? "" : it.cantidad}
                            onChange={(e) => updateItemQty(idx, e.target.value)}
                            onFocus={(e) => { if (e.target.value === "0") e.target.value = ""; }}
                            className="w-20 mx-auto text-center font-bold h-8"
                            data-testid={`qty-${idx}`}
                          />
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm">{fmt(it.costo_unitario)}</TableCell>
                      <TableCell className="text-right font-semibold">{fmt(it.subtotal)}</TableCell>
                      {!isConverted && (
                        <TableCell>
                          <Button
                            variant="ghost" size="icon"
                            className="h-7 w-7 text-red-400 hover:text-red-600"
                            onClick={() => removeItem(idx)}
                            data-testid={`remove-item-${idx}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {items.length > 0 && (
                    <TableRow className="bg-slate-50 font-bold">
                      <TableCell colSpan={7}></TableCell>
                      <TableCell className="text-center">{items.reduce((s, i) => s + (i.cantidad || 0), 0)}</TableCell>
                      <TableCell className="text-right">TOTAL</TableCell>
                      <TableCell className="text-right text-blue-700 text-lg">{fmt(detail.total)}</TableCell>
                      {!isConverted && <TableCell></TableCell>}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Add Item Dialog */}
          <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Añadir Artículo al Pedido</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-500">Selecciona un producto del inventario para agregarlo manualmente al pedido.</p>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Producto</Label>
                  <Select value={selectedItemId} onValueChange={setSelectedItemId}>
                    <SelectTrigger data-testid="select-add-item"><SelectValue placeholder="Seleccionar producto..." /></SelectTrigger>
                    <SelectContent>
                      {allItems
                        .filter(ai => !(detail?.items || []).some(di => di.item_id === ai.id))
                        .map(ai => (
                          <SelectItem key={ai.id} value={ai.id}>
                            {ai.clave} — {ai.descripcion} (Stock: {ai.stock_actual})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Cantidad a pedir</Label>
                  <Input
                    type="number" min="1" step="1"
                    value={addQty}
                    onChange={(e) => setAddQty(Math.max(1, parseInt(e.target.value) || 1))}
                    data-testid="add-item-qty"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddItem(false)}>Cancelar</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleAddItem} data-testid="confirm-add-item-btn">
                  <Plus className="w-4 h-4 mr-2" /> Agregar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    );
  }

  // ============ LIST VIEW ============
  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="pedidos-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <ModuleHeaderIcon icon={ShoppingCart} />
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Pedidos</h1>
              <p className="text-slate-500">Genera pedidos automáticos basados en stock mínimo</p>
            </div>
          </div>
          <Button onClick={() => setShowNewDialog(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-pedido-btn">
            <Plus className="w-4 h-4 mr-2" /> Generar Pedido
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-10 text-slate-400">Cargando...</div>
        ) : pedidos.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-slate-400">
            <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">Sin pedidos</p>
            <p className="text-sm">Genera un pedido automático para productos con stock bajo</p>
          </CardContent></Card>
        ) : (
          <div className="grid gap-4">
            {pedidos.map(p => (
              <Card key={p.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => openDetail(p)} data-testid={`pedido-card-${p.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-lg ${p.estatus === "convertido" ? "bg-green-100" : "bg-amber-100"}`}>
                        {p.estatus === "convertido" ? <CheckCircle className="w-6 h-6 text-green-600" /> : <ShoppingCart className="w-6 h-6 text-amber-600" />}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800">{p.nombre}</h3>
                        <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(p.fecha)}</span>
                          {p.responsable && <span className="flex items-center gap-1"><User className="w-3 h-3" /> {p.responsable}</span>}
                          <span>{(p.items || []).length} productos</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-xs text-slate-400">Total</p>
                        <p className="text-lg font-bold text-blue-700">{fmt(p.total)}</p>
                      </div>
                      <Badge className={p.estatus === "convertido" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                        {p.estatus === "convertido" ? "Convertido" : "Pendiente"}
                      </Badge>
                      <Eye className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Generate Order Dialog */}
        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Generar Pedido</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-500">Se incluirán automáticamente todos los productos con stock por debajo del mínimo.</p>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Nombre</Label><Input value={newForm.nombre} onChange={e => setNewForm(p => ({ ...p, nombre: e.target.value }))} placeholder="Ej: Pedido Abril 2026" data-testid="new-pedido-nombre" /></div>
              <div className="space-y-2"><Label>Responsable</Label><Input value={newForm.responsable} onChange={e => setNewForm(p => ({ ...p, responsable: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Notas</Label><Input value={newForm.notas} onChange={e => setNewForm(p => ({ ...p, notas: e.target.value }))} placeholder="Opcional..." /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewDialog(false)}>Cancelar</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleGenerar} data-testid="generar-pedido-btn">
                <AlertTriangle className="w-4 h-4 mr-2" /> Generar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Pedidos;
